import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { GroupRecord } from "~/types/api/dex/rebate";
import { formatPrice } from "~/utils/number-format";
import LottieSticker from "~/components/LottieSticker";
import { IconProfit } from "~/components/Iconfont/Iconfont";
import { useNavigate } from "react-router";

type GroupListProps = {
    groupList: GroupRecord[] | undefined;
};

const BG_LIST = [
    "linear-gradient(180deg, #FFC36D 0%, #FF7B23 100%)",
    "linear-gradient(180deg, #FF9A7C 0%, #FF2006 100%)",
    "linear-gradient(180deg, #A6D1FF 0%, #007AFF 100%)",
    "linear-gradient(180deg, #A5FF94 0%, #21C004 100%)",
    "linear-gradient(180deg, #D1ADFF 0%, #9747FF 100%)",
    "linear-gradient(180deg, #FC85FF 0%, #B00FB4 100%)",
    "linear-gradient(180deg, #FFDE79 0%, #F6BD00 100%)",
];

const GroupList: React.FC<GroupListProps> = ({ groupList = [] }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (groupList.length === 0) {
        return (
            <div className="py-10 px-3 flex flex-col items-center">
                <LottieSticker
                    lottieName="no-result"
                    className="w-[80px] h-[80px]"
                />
                <h3 className="mt-3 text-text_1 text-body1 font-semibold">
                    {t("group_no_reward_record")}
                </h3>
                <p className="mt-1 text-text_2 text-body3 text-center">
                    {t("group_no_reward_record_tips")}
                </p>

                <div
                    className={clsx(
                        "h-11 flex items-center justify-center py-3 px-9 mt-3",
                        "rounded-[12px] bg-AlphaOrange dark:bg-DarkAlphaOrange cursor-pointer"
                    )}
                    onClick={() => {
                        navigate("/profit-tips");
                    }}
                >
                    <IconProfit className="text-h5 text-text_brand mr-2 -mt-0.5" />
                    <span className="text-body4 text-text_brand font-semibold">
                        {t("rebate_profit_tips")}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-[12px] bg-bg_0 border-[0.5px] mt-2 border-border_2">
            <div className="px-[12px] h-[32px] flex items-end pb-1 justify-between text-direction1 font-Regular text-text_3">
                <div className="text-left w-[140px]">{t("group")}</div>
                <div className="text-right w-[128px]">{t("reward")}</div>
            </div>
            <div>
                {groupList.map((item, index) => {
                    const noBorder = index === 0;
                    return (
                        <div
                            className="flex items-stretch cursor-pointer h-16"
                            key={index}
                        >
                            <div className="pl-3 flex-1 w-0 flex items-stretch">
                                <div className="flex-none py-3.5 mr-3">
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center"
                                        style={{
                                            background:
                                                BG_LIST[index % BG_LIST.length],
                                        }}
                                    >
                                        <span className="text-White text-h5 font-semibold">
                                            {item?.group_name?.[0] || "G"}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className={clsx(
                                        "flex-1 flex flex-col justify-center",
                                        {
                                            "border-t-[0.5px] border-border_2":
                                                !noBorder,
                                        }
                                    )}
                                >
                                    <div className="flex flex-col items-start">
                                        <div className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center ">
                                            <span className="whitespace-nowrap text-ellipsis overflow-hidden max-w-[45vw]">
                                                {item.group_name}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                key={index}
                                className={clsx(
                                    "flex-1 w-0 flex items-center justify-end pr-3",
                                    {
                                        "border-t-[0.5px] border-border_2":
                                            !noBorder,
                                    }
                                )}
                            >
                                <div className="flex flex-col items-end justify-center h-full">
                                    <span className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center">
                                        +{formatPrice(item.value)}
                                    </span>

                                    {Number(item.up_value) > 0 && (
                                        <span className="text-body4 text-text_1">
                                            +{formatPrice(item.up_value)}{" "}
                                            <span className="text-body4 text-text_2">
                                                {t("today")}
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GroupList;
